import { Button, Form, type FormProps, Modal, Space, TreeSelect, Typography } from 'antd'
import React, { useEffect, useState } from 'react'

import { BundleService, type BundleUpdateParams, type BundleWithFolders } from '@services/BundleService'
import { useNotifications } from '@store/notifications'

type ModalProps = {
    selectedId: string | null
    onClose: () => void
    onSuccess: () => void
}

type TreeItem = {
    disabled?: boolean
    halfChecked?: boolean
    label: string
    value: string
}

type BundleResourceFieldType = {
    communityResourceFolderIds: TreeItem[]
}

const normalize = (arrayToNormalize: Array<any>, key: string) => {
    return arrayToNormalize.reduce((collector, item) => {
        const returnable = collector
        returnable[item[key]] = { ...item }
        return returnable
    }, {})
}

type FlatTreeOption = {
    title: string
    value: string
    key: string
    parentId: string
}

type TreeOption = {
    title: string
    value: string
    key: string
    children: TreeOption[]
}

type TreeOptions = TreeOption[]

const organizeFolders = (resourceFolders: FlatTreeOption[]): TreeOptions => {
    const normalized = normalize(resourceFolders, 'key')
    const sorted = Object.keys(normalized).reduce((collector: TreeOption[], key) => {
        const folder = normalized[key]

        if (folder.parentId) {
            if (!normalized[folder.parentId].children) {
                normalized[folder.parentId].children = []
            }
            normalized[folder.parentId].children.push(folder)
        } else {
            return [...collector, folder]
        }

        return collector
    }, [])

    return sorted
}

export const AssociateResourceModal: React.FC<ModalProps> = ({ selectedId, onClose, onSuccess }) => {
    const [bundle, setBundle] = useState<BundleWithFolders>()
    const [resourceTreeOptions, setResourceTreeOptions] = useState<TreeOptions>()
    const { showNotification } = useNotifications()

    useEffect(() => {
        const fetchBundle = async () => {
            try {
                const results = await BundleService.get(selectedId!)
                setBundle(results)
            } catch (err: any) {
                showNotification({ title: 'Could not retrieve bundle', type: 'error', description: err.toString() })
            }
        }

        if (selectedId) {
            fetchBundle()
        }
    }, [selectedId])

    useEffect(() => {
        const fetchResourceFolders = async (creatorId: string) => {
            try {
                const results = await BundleService.listResourceFolders(creatorId)
                const treeOptions = results.map(folder => {
                    return {
                        title: folder.name,
                        value: folder.id,
                        key: folder.id,
                        parentId: folder.parentId
                    }
                })
                const organized = organizeFolders(treeOptions)
                setResourceTreeOptions(organized)
            } catch (err: any) {
                showNotification({ title: 'Could not retrieve bundle', type: 'error', description: err.toString() })
            }
        }

        if (bundle?.creatorId) {
            fetchResourceFolders(bundle.creatorId)
        }
    }, [bundle])

    const onFinish: FormProps<BundleResourceFieldType>['onFinish'] = async values => {
        const body: BundleUpdateParams = {
            bundleResources: values.communityResourceFolderIds.map(item => ({
                communityResourceFolderId: item.value
            }))
        }

        try {
            await BundleService.update(selectedId!, body)
            showNotification({ title: 'Bundle updated successfully' })
            onSuccess()
            onClose()
        } catch (err: any) {
            showNotification({ title: 'Bundle update failed', type: 'error', description: err.toString() })
        }
    }

    const isOpen = !!selectedId

    if (!bundle || !resourceTreeOptions) {
        return null
    }

    const checkboxValues = bundle.bundleResources.map(bundleResource => bundleResource.communityResourceFolderId)

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            <Form
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                layout="horizontal"
                disabled={false}
                onFinish={onFinish}
            >
                <Space direction="vertical">
                    <Typography.Text>Checked items will be displayed at the top of the bundle</Typography.Text>
                    <Typography.Text style={{ fontSize: '12px' }}>
                        You do not need to check a parent item and its children for it to be displayed. Doing so will
                        display all items at the top of the bundle
                    </Typography.Text>
                    <Form.Item label="Resource Folders" name="communityResourceFolderIds" initialValue={checkboxValues}>
                        <TreeSelect treeData={resourceTreeOptions} treeCheckStrictly treeCheckable />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <Space>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                Submit
                            </Button>
                        </Space>
                    </Form.Item>
                </Space>
            </Form>
        </Modal>
    )
}
